import React, { FC, useContext, useMemo } from 'react';
import styles from './index.module.scss';
import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import { Spin } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { apiV2PaintingDashboardEnergyTrendPost } from '@maxtropy/device-customer-apis-v2';
import { DateContext } from '../../../index';
import { IndexTimeActive } from '../../SwitchDate';
import { getEnergyTrendChartOption } from './charts';

const EnergyTrend: FC = () => {
  const theme = useTheme();
  const { activeKey, selectedDate } = useContext(DateContext);

  // 构建请求参数
  const requestParams = useMemo(() => {
    const timeResolution = activeKey === IndexTimeActive.THIS_MONTH ? 'M' : 'Y';
    const unit = activeKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year';
    const startTime = selectedDate.startOf(unit).valueOf();
    // 结束时间：前一天23:59:59（如果是当前月份/年份），否则为月末/年末
    const isCurrentPeriod =
      selectedDate.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY') ===
      dayjs().format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY');
    const endTime = isCurrentPeriod
      ? dayjs().subtract(1, 'day').endOf('day').valueOf()
      : selectedDate.endOf(unit).valueOf();
    return { startTime, endTime, timeResolution };
  }, [activeKey, selectedDate]);

  const { data, loading } = useRequest(() => apiV2PaintingDashboardEnergyTrendPost(requestParams), {
    refreshDeps: [requestParams],
  });

  const timeFormat = activeKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year';

  // 转换数据格式
  const trendData = useMemo(() => {
    if (!data?.electricity?.points?.length) return [];

    // 获取所有时间点
    const timestamps = data.electricity.points.map(p => p.timestamp);

    // 根据时间粒度格式化 x 轴日期
    // 按年：YYYY-MM，按月：MM-DD
    const dateFormat = activeKey === IndexTimeActive.THIS_MONTH ? 'MM-DD' : 'YYYY-MM';
    return timestamps.map((ts, i) => ({
      date: dayjs(ts).format(dateFormat),
      flow: data.electricity?.points?.[i]?.value ?? null,
      pressure: data.gas?.points?.[i]?.value ?? null,
      air: data.air?.points?.[i]?.value ?? null,
    }));
  }, [data, activeKey]);

  const trendChartOption = useMemo(() => {
    if (!trendData.length) return null;
    return getEnergyTrendChartOption(trendData, timeFormat);
  }, [trendData, timeFormat]);

  if (loading) {
    return (
      <div className={styles.loading_wrapper}>
        <Spin />
      </div>
    );
  }

  if (!trendData.length) {
    return <Empty />;
  }

  return (
    <div className={styles.trend_chart}>
      <EchartsWithTheme
        theme={theme}
        style={{ height: '100%', width: '100%' }}
        option={trendChartOption}
        empty={{ isShowEmpty: !trendData.length, emptyNode: <Empty /> }}
      />
    </div>
  );
};

export default EnergyTrend;
